import type { Category } from "../../../../shared/types";
import { CATEGORY_LABELS } from "../../../../shared/types";
import { CheckIcon, PlusIcon, CalendarIcon, TargetIcon } from "../../../../ui/icons";

export interface ActivityItem {
  readonly type:
    | "task_completed"
    | "task_created"
    | "event_created"
    | "habit_checked";
  readonly title: string;
  readonly category: Category;
  readonly timestamp: number;
  readonly date: string;
}

interface ActivityItemRowProps {
  readonly item: ActivityItem;
  readonly index: number;
}

const ICON_MAP: Record<ActivityItem["type"], React.ReactNode> = {
  task_completed: <CheckIcon className="w-3.5 h-3.5" />,
  task_created: <PlusIcon className="w-3.5 h-3.5" />,
  event_created: <CalendarIcon className="w-3.5 h-3.5" />,
  habit_checked: <TargetIcon className="w-3.5 h-3.5" />,
};

const LABEL_MAP: Record<ActivityItem["type"], (title: string) => string> = {
  task_completed: (title) => `Tâche complétée : ${title}`,
  task_created: (title) => `Tâche créée : ${title}`,
  event_created: (title) => `Événement créé : ${title}`,
  habit_checked: (title) => `Habitude vérifiée : ${title}`,
};

function getRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes}m`;
  if (hours < 24) return `il y a ${hours}h`;
  if (days < 7) return `il y a ${days}j`;
  return "il y a longtemps";
}

export function ActivityItemRow({ item, index }: ActivityItemRowProps) {
  return (
    <div
      key={`${item.date}_${item.type}_${index}`}
      className="bg-surface border border-border rounded-lg p-3 flex gap-3"
    >
      <div className="shrink-0 w-8 h-8 rounded-full bg-surface-alt flex items-center justify-center text-sm">
        {ICON_MAP[item.type]}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-ink truncate">
          {LABEL_MAP[item.type](item.title)}
        </p>
        <div className="flex gap-2 mt-1">
          <span className="text-[10px] text-ink-faint">
            {CATEGORY_LABELS[item.category]}
          </span>
          <span className="text-[10px] text-ink-faint">
            {getRelativeTime(item.timestamp)}
          </span>
        </div>
      </div>
    </div>
  );
}
